import React, { useState, useMemo } from "react";
import { useLocation } from "wouter";
import { Search, ArrowUp, Activity } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";

interface OrderMetric {
  id: string;
  title: string;
  value: string;
  subtitle: string;
  icon: string;
  color: string;
  trend?: string;
}

const MOCK_METRICS: OrderMetric[] = [
  {
    id: "total-quantity",
    title: "Total Order Quantity",
    value: "0",
    subtitle: "All active orders",
    icon: "📦",
    color: "text-cyan-400",
    trend: "active",
  },
  {
    id: "in-stock",
    title: "Currently in Stock",
    value: "0",
    subtitle: "Available inventory",
    icon: "📊",
    color: "text-cyan-400",
    trend: "available",
  },
  {
    id: "loaded-mine",
    title: "Loaded at Mine",
    value: "0",
    subtitle: "From source location",
    icon: "⛏️",
    color: "text-orange-400",
    trend: "loaded",
  },
  {
    id: "at-lions-park",
    title: "At Lions Park",
    value: "0",
    subtitle: "Staging area stock",
    icon: "🏢",
    color: "text-purple-400",
    trend: "staging",
  },
  {
    id: "offloaded-port",
    title: "Offloaded at Port",
    value: "0",
    subtitle: "0% of total orders",
    icon: "⚓",
    color: "text-cyan-400",
    trend: "offloaded",
  },
  {
    id: "vessel-eta",
    title: "Vessel Arrival ETA",
    value: "No vessel",
    subtitle: "N/A",
    icon: "🚢",
    color: "text-cyan-400",
    trend: "eta",
  },
  {
    id: "qty-per-day",
    title: "QTY Per Day Required",
    value: "0",
    subtitle: "To meet ETA",
    icon: "📈",
    color: "text-orange-400",
    trend: "daily",
  },
  {
    id: "trucks-per-day",
    title: "Trucks Required Per Day",
    value: "0",
    subtitle: "Based on 30 tons/truck",
    icon: "🚚",
    color: "text-pink-400",
    trend: "trucks",
  },
];

interface OrderDetail {
  id: string;
  orderNumber: string;
  quantity: number;
  status: string;
  destination: string;
}

const MOCK_TOP_ORDERS: OrderDetail[] = [
  {
    id: "1",
    orderNumber: "ORD-001",
    quantity: 1500,
    status: "Loading",
    destination: "Lions Park",
  },
  {
    id: "2",
    orderNumber: "ORD-002",
    quantity: 1200,
    status: "In Transit",
    destination: "Port",
  },
  {
    id: "3",
    orderNumber: "ORD-003",
    quantity: 800,
    status: "Pending",
    destination: "Staging",
  },
];

export default function OrderOverview() {
  const [, navigate] = useLocation();
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterDestination, setFilterDestination] = useState("all");

  const filteredOrders = useMemo(() => {
    return MOCK_TOP_ORDERS.filter((order) => {
      const matchesSearch =
        order.orderNumber.toLowerCase().includes(searchQuery.toLowerCase()) ||
        order.destination.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus =
        filterStatus === "all" || order.status === filterStatus;
      const matchesDestination =
        filterDestination === "all" ||
        order.destination === filterDestination;

      return matchesSearch && matchesStatus && matchesDestination;
    });
  }, [searchQuery, filterStatus, filterDestination]);

  const MetricCard = ({ metric }: { metric: OrderMetric }) => (
    <Card
      className="cursor-pointer hover-elevate active-elevate-2 bg-[hsl(215,28%,10%)] border border-[hsl(215,28%,18%)] transition-all"
      onClick={() => navigate(`/orders/${metric.id}`)}
      data-testid={`metric-card-${metric.id}`}
    >
      <div className="p-4 space-y-3">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs text-muted-foreground mb-1">
              {metric.title}
            </p>
            <div className={`text-2xl font-bold ${metric.color}`}>
              {metric.value}
            </div>
          </div>
          <span className="text-2xl">{metric.icon}</span>
        </div>
        <p className="text-xs text-[hsl(215,20%,55%)]">{metric.subtitle}</p>
      </div>
    </Card>
  );

  return (
    <div className="space-y-6">
      {/* Header with Live Data badge */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-[hsl(187,100%,42%)] flex items-center gap-2">
            <Activity className="w-8 h-8" />
            Order Overview
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Complete business operations dashboard - Real-time metrics
          </p>
        </div>
        <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-[hsl(142,76%,36%)]/15 border border-[hsl(142,76%,36%)]">
          <span className="w-2 h-2 rounded-full bg-[hsl(142,76%,36%)]"></span>
          <span className="text-xs font-medium text-[hsl(142,76%,50%)]">
            Live Data
          </span>
        </div>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {MOCK_METRICS.map((metric) => (
          <MetricCard key={metric.id} metric={metric} />
        ))}
      </div>

      {/* Overall Loading Progress */}
      <Card className="bg-[hsl(215,28%,10%)] border border-[hsl(215,28%,18%)]">
        <div className="p-6">
          <h2 className="text-lg font-bold text-[hsl(187,100%,42%)] flex items-center gap-2 mb-4">
            <ArrowUp className="w-5 h-5" />
            Overall Loading Progress
          </h2>

          <div className="space-y-4">
            <div>
              <div className="flex justify-between mb-2">
                <span className="text-sm font-medium">Total Progress</span>
                <span className="text-sm text-muted-foreground">0%</span>
              </div>
              <Progress value={0} className="h-2" />
            </div>

            <div className="grid grid-cols-3 gap-4 mt-6 pt-4 border-t border-[hsl(215,28%,18%)]">
              <div className="text-center">
                <div className="text-2xl font-bold text-white">0</div>
                <div className="text-xs text-muted-foreground mt-1">
                  Total Tons
                </div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-white">0</div>
                <div className="text-xs text-muted-foreground mt-1">Loaded</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-orange-400">0</div>
                <div className="text-xs text-muted-foreground mt-1">
                  Remaining
                </div>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Top Orders by Quantity */}
      <Card className="bg-[hsl(215,28%,10%)] border border-[hsl(215,28%,18%)]">
        <div className="p-6">
          <h2 className="text-lg font-bold text-[hsl(187,100%,42%)] mb-4">
            Top Orders by Quantity
          </h2>

          {/* Search and Filters */}
          <div className="space-y-4 mb-6">
            <div className="flex gap-3">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[hsl(215,20%,45%)]" />
                <Input
                  placeholder="Search orders, destination..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 bg-[hsl(215,28%,14%)] border-[hsl(215,28%,18%)] text-white placeholder:text-[hsl(215,20%,45%)]"
                  data-testid="input-search-orders"
                />
              </div>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-32 bg-[hsl(215,28%,14%)] border-[hsl(215,28%,18%)]">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="Pending">Pending</SelectItem>
                  <SelectItem value="Loading">Loading</SelectItem>
                  <SelectItem value="In Transit">In Transit</SelectItem>
                </SelectContent>
              </Select>
              <Select
                value={filterDestination}
                onValueChange={setFilterDestination}
              >
                <SelectTrigger className="w-32 bg-[hsl(215,28%,14%)] border-[hsl(215,28%,18%)]">
                  <SelectValue placeholder="Destination" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Destinations</SelectItem>
                  <SelectItem value="Staging">Staging</SelectItem>
                  <SelectItem value="Lions Park">Lions Park</SelectItem>
                  <SelectItem value="Port">Port</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Orders Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[hsl(215,28%,18%)]">
                  <th className="text-left py-3 px-2 font-semibold text-muted-foreground">
                    Order Number
                  </th>
                  <th className="text-left py-3 px-2 font-semibold text-muted-foreground">
                    Quantity
                  </th>
                  <th className="text-left py-3 px-2 font-semibold text-muted-foreground">
                    Status
                  </th>
                  <th className="text-left py-3 px-2 font-semibold text-muted-foreground">
                    Destination
                  </th>
                  <th className="text-right py-3 px-2 font-semibold text-muted-foreground">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredOrders.length > 0 ? (
                  filteredOrders.map((order) => (
                    <tr
                      key={order.id}
                      className="border-b border-[hsl(215,28%,18%)] hover:bg-[hsl(215,28%,14%)] transition-colors"
                      data-testid={`row-order-${order.id}`}
                    >
                      <td className="py-3 px-2 text-white font-medium">
                        {order.orderNumber}
                      </td>
                      <td className="py-3 px-2 text-white">
                        {order.quantity.toLocaleString()} tons
                      </td>
                      <td className="py-3 px-2">
                        <span
                          className={`px-2 py-1 rounded text-xs font-medium ${
                            order.status === "Loading"
                              ? "bg-orange-500/20 text-orange-400"
                              : order.status === "In Transit"
                                ? "bg-blue-500/20 text-blue-400"
                                : "bg-yellow-500/20 text-yellow-400"
                          }`}
                        >
                          {order.status}
                        </span>
                      </td>
                      <td className="py-3 px-2 text-muted-foreground">
                        {order.destination}
                      </td>
                      <td className="py-3 px-2 text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/orders/${order.id}`)}
                          data-testid={`button-view-order-${order.id}`}
                        >
                          View
                        </Button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={5}
                      className="py-8 text-center text-muted-foreground"
                    >
                      No orders found matching your criteria
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </Card>
    </div>
  );
}
